import { useState, useCallback, useRef } from "react";
import ReactECharts from "echarts-for-react";
import { makeRequest } from "app/utils/makeRequest";
import { getSessionStore } from "app/utils/sessionStore";
import {
  createGradient,
  getThemeColors,
  getUSANumberFormat,
  getTooltipColors,
  projectionBSOptions,
  projectionKPOptions,
  projectionSDOptions,
  projectionPFOptions,
} from "app/utils/utils";
import { intToString } from "app/uicomponents/macrocomponents/PolicySummary/config";
import { GoTriangleDown, GoTriangleUp } from "react-icons/go";
import {
  ProjectionFilters,
  ProjectionKPFilters,
  ProjectionPFFilters,
  ProjectionSDFilters,
  ProposalTypes,
  THEME,
} from "app/utils/types";
import ScreenLoader from "app/uicomponents/microcomponents/ScreenLoader";
import { useSelector } from "react-redux";
import { getAgentBranding } from "app/redux/features/agent-branding";
import { Row } from "primereact/row";
import { Column } from "primereact/column";
import {
  headerColumnGroupPFProjection,
  headerColumnGroupProjection,
  projectionColumnJson,
  projectionPFColumnJson,
} from "../../../Data/projectionColumnJson";
import InsuredFilter from "app/uicomponents/macrocomponents/InteractiveProposal/Funding/InsuredFilter";
import InsuredTable from "app/uicomponents/macrocomponents/InteractiveProposal/Funding/InsuredTable";
import {
  headerColumnGroupSDProjection,
  projectionSDColumnJson,
} from "../../../Data/projectionSDColumnJson";
import { projectionKPColumnJson } from "../../../Data/projectionKPColumnJson";
import CommentLayout from "app/uicomponents/macrocomponents/Comments/CommentLayout";
import { useIPContext } from "../InteractiveProposalProvider";
import { useComments } from "app/uicomponents/macrocomponents/Comments/CommentContext";
import { Accordion, AccordionTab } from "primereact/accordion";
import ChartFilter from "../Common/ChartFilter";
import OutsideCollateralRequirement from "./Projection/OutsideCollateralRequirement";
import { getChartLabels } from "lib/utils";

let selectedProjectionData: any = null;
const InteractiveProposalProjection = () => {
  const [chartDataSets, setChartDataSets] = useState<any>(null);
  const [graphData, setGraphData] = useState<any>(null);
  const [selectedValues, setSelectedValues] = useState<any>(null);
  const [tableData, setTableData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const selectedSeriesRef = useRef<string | null>(null);
  const zoomStateRef = useRef<{ start: number; end: number } | null>(null);
  const { selectedVersion, summaryData: proposalSummary } = useIPContext();
  const proposalId = selectedVersion?.id ?? getSessionStore("interactiveProposalID");
  const globalTheme = getSessionStore("globalTheme");
  const { settingThemeData } = useSelector(getAgentBranding);
  const { branding } = getSessionStore("lookUpResponse");
  const { primaryColor, secondaryColor } = getThemeColors(branding);
  const { setIsCurrPageLoading } = useComments();
  const { selectedChartFilter } = useIPContext();
  const contactBranding = useSelector((state: any) => state.contactBranding);
  const planInfo = getSessionStore("planInfo");
  const brandingFromPlanInfo = planInfo?.branding;
  const isDarkMode = globalTheme === THEME.DARK;

  // Use branding from planInfo if available, otherwise use Redux state
  const finalContactBranding = brandingFromPlanInfo || contactBranding;
  const tooltipColors = getTooltipColors(finalContactBranding);
  const insuredDetails = proposalSummary?.insuredDetails?.[0] ?? proposalSummary?.primaryContact;

  const calculateChartData = useCallback(
    (chartsData: any[], funding: string[], brandingColor: string) => {
      const datasets = [];
      if (proposalSummary?.proposalType === ProposalTypes.SplitDollar) {
        if (funding.includes(ProjectionSDFilters.INCOME)) {
          datasets.push({
            label: "Income",
            borderColor: projectionSDOptions(brandingColor)[0].indicatorColor,
            backgroundColor: projectionSDOptions(brandingColor)[0].indicatorColor,
            data: chartsData?.map((item: any) => item?.income),
            fill: true,
            type: "bar",
            barWidth: 10,
            itemStyle: {
              borderRadius: [10, 10, 0, 0], // top-left & top-right = 10px
            },
          });
        }
        if (funding.includes(ProjectionSDFilters.ACCOUNT_VALUE)) {
          datasets.push({
            label: "Account Value",
            borderColor: projectionSDOptions(brandingColor)[1].indicatorColor,
            backgroundColor: projectionSDOptions(brandingColor)[1].indicatorColor,
            data: chartsData?.map((item: any) => item?.accountValue),
            fill: true,
          });
        }
        if (funding.includes(ProjectionSDFilters.CASH_VALUE)) {
          datasets.push({
            label: "Surrender Value",
            type: "line",
            borderColor: projectionSDOptions(brandingColor)[2].indicatorColor,
            backgroundColor: (context: any) =>
              createGradient(context, projectionSDOptions(brandingColor)[2].indicatorColor),
            data: chartsData?.map((item: any) => item?.cashValue),
            fill: true,
          });
        }
        if (funding.includes(ProjectionSDFilters.DEATH_BENEFIT)) {
          datasets.push({
            label: "Death Benefit",
            type: "line",
            borderColor: projectionSDOptions(brandingColor)[3].indicatorColor,
            backgroundColor: (context: any) =>
              createGradient(context, projectionSDOptions(brandingColor)[3].indicatorColor),
            data: chartsData?.map((item: any) => item?.deathBenefit),
            fill: true,
          });
        }
        if (funding.includes(ProjectionSDFilters.LOAN)) {
          datasets.push({
            label: "Financing",
            type: "line",
            borderColor: projectionSDOptions(brandingColor)[4].indicatorColor,
            tooltipColor: projectionSDOptions(brandingColor)[4].indicatorColor,
            backgroundColor: (context: any) =>
              createGradient(context, projectionSDOptions(brandingColor)[4].indicatorColor),
            data: chartsData?.map((item: any) => item?.loan),
            fill: true,
          });
        }
      }
      if (proposalSummary?.proposalType === ProposalTypes.KeyPersonInsurance) {
        if (funding.includes(ProjectionKPFilters.DEATH_BENEFIT)) {
          datasets.push({
            label: "Death Benefit",
            borderColor: projectionKPOptions(brandingColor)[0].indicatorColor,
            backgroundColor: (context: any) =>
              createGradient(context, projectionKPOptions(brandingColor)[0].indicatorColor),
            data: chartsData?.map((item: any) => item?.deathBenefit),
            fill: true,
          });
        }
        if (funding.includes(ProjectionKPFilters.CI_BENEFIT)) {
          datasets.push({
            label: "Living Benefit",
            borderColor: projectionKPOptions(brandingColor)[1].indicatorColor,
            backgroundColor: (context: any) =>
              createGradient(context, projectionKPOptions(brandingColor)[1].indicatorColor),
            data: chartsData?.map((item: any) => item?.ciBenefit),
            fill: true,
          });
        }
        if (funding.includes(ProjectionKPFilters.DISABILITY_BENEFIT)) {
          datasets.push({
            label: "Disability Benefit",
            borderColor: projectionKPOptions(brandingColor)[2].indicatorColor,
            backgroundColor: (context: any) =>
              createGradient(context, projectionKPOptions(brandingColor)[2].indicatorColor),
            data: chartsData?.map((item: any) => item?.disabilityBenefit),
            fill: true,
          });
        }
        if (funding.includes(ProjectionKPFilters.KEY_EMPLOYEE)) {
          datasets.push({
            label: "Key Employee Value",
            borderColor: projectionKPOptions(brandingColor)[3].indicatorColor,
            backgroundColor: (context: any) =>
              createGradient(context, projectionKPOptions(brandingColor)[3].indicatorColor),
            data: chartsData?.map((item: any) => item?.valuation),
            fill: true,
          });
        }
      }
      if (proposalSummary?.proposalType === ProposalTypes.BuySell) {
        if (funding.includes(ProjectionFilters.BUSINESS_VALUE)) {
          datasets.push({
            label: "Shareholder Value",
            borderColor: projectionBSOptions(brandingColor)[2].indicatorColor,
            tooltipColor: projectionBSOptions(brandingColor)[2].indicatorColor,
            backgroundColor: (context: any) =>
              createGradient(context, projectionBSOptions(brandingColor)[2].indicatorColor),
            data: chartsData?.map((item: any) => item?.businessValuation),
            fill: true,
          });
        }
        if (funding.includes(ProjectionFilters.DEATH_BENEFIT)) {
          datasets.push({
            label: "Death Benefit",
            borderColor: projectionBSOptions(brandingColor)[0].indicatorColor,
            backgroundColor: (context: any) =>
              createGradient(context, projectionBSOptions(brandingColor)[0].indicatorColor),
            data: chartsData?.map((item: any) => item?.deathBenefit),
            fill: true,
          });
        }
        if (funding.includes(ProjectionFilters.DISABILITY_BENEFIT)) {
          datasets.push({
            label: "Disability Benefit",
            borderColor: projectionBSOptions(brandingColor)[1].indicatorColor,
            backgroundColor: (context: any) =>
              createGradient(context, projectionBSOptions(brandingColor)[1].indicatorColor),
            data: chartsData?.map((item: any) => item?.disabilityBenefit),
            fill: true,
          });
        }
      }
      if (proposalSummary?.proposalType === ProposalTypes.PremiumFinance) {
        if (funding.includes(ProjectionPFFilters.ACCUMULATED_VALUE)) {
          datasets.push({
            label: "Account Value",
            borderColor: projectionPFOptions(brandingColor)[0].indicatorColor,
            backgroundColor: (context: any) =>
              createGradient(context, projectionPFOptions(brandingColor)[0].indicatorColor),
            data: chartsData?.map((item: any) => item?.accumulatedValue),
            fill: true,
          });
        }
        if (funding.includes(ProjectionPFFilters.CASH_VALUE)) {
          datasets.push({
            label: "Surrender Value",
            borderColor: projectionPFOptions(brandingColor)[1].indicatorColor,
            backgroundColor: (context: any) =>
              createGradient(context, projectionPFOptions(brandingColor)[1].indicatorColor),
            data: chartsData?.map((item: any) => item?.cashValue),
            fill: true,
          });
        }
        if (funding.includes(ProjectionPFFilters.STRESSED_CASH_VALUE)) {
          datasets.push({
            label: "Stress Surrender Value",
            borderColor: projectionPFOptions(brandingColor)[2].indicatorColor,
            backgroundColor: (context: any) =>
              createGradient(context, projectionPFOptions(brandingColor)[2].indicatorColor),
            data: chartsData?.map((item: any) => item?.stressedCashValue),
            fill: true,
          });
        }
        if (funding.includes(ProjectionPFFilters.LOAN)) {
          datasets.push({
            label: "Financing",
            borderColor: projectionPFOptions(brandingColor)[3].indicatorColor,
            backgroundColor: (context: any) =>
              createGradient(context, projectionPFOptions(brandingColor)[3].indicatorColor),
            data: chartsData?.map((item: any) => item?.loan),
            fill: true,
          });
        }
        if (funding.includes(ProjectionPFFilters.DEATH_BENEFIT)) {
          datasets.push({
            label: "Death Benefit",
            borderColor: projectionPFOptions(brandingColor)[4].indicatorColor,
            backgroundColor: (context: any) =>
              createGradient(context, projectionPFOptions(brandingColor)[4].indicatorColor),
            data: chartsData?.map((item: any) => item?.deathBenefit),
            fill: true,
          });
        }
        if (funding.includes(ProjectionPFFilters.ANNUAL_INCOME)) {
          datasets.push({
            label: "Annual Income",
            type: "bar",
            barWidth: 10,
            itemStyle: {
              borderRadius: [10, 10, 0, 0], // top-left & top-right = 10px
            },
            borderColor: projectionPFOptions(brandingColor)[5].indicatorColor,
            backgroundColor: (context: any) =>
              createGradient(context, projectionPFOptions(brandingColor)[5].indicatorColor),
            data: chartsData?.map((item: any) => item?.annualIncome),
            fill: true,
          });
        }
        if (funding.includes(ProjectionPFFilters.TOTAL_INCOME)) {
          datasets.push({
            label: "Total Income",
            borderColor: projectionPFOptions(brandingColor)[6].indicatorColor,
            backgroundColor: (context: any) =>
              createGradient(context, projectionPFOptions(brandingColor)[6].indicatorColor),
            data: chartsData?.map((item: any) => item?.totalIncome),
            fill: true,
          });
        }
      }
      return datasets;
    },
    [],
  );
  // Define the header structure headerColumnGroupProjection
  const isDark = globalTheme === THEME.DARK;
  const kpProjectionTableClassName = `
    .insured-table .p-datatable-thead > tr:nth-child(1) > th:not(.p-sortable-column.p-highlight) {
      background: ${isDark ? "#2A2A3D" : "#F7F7F8"} !important;
    }
  `;

  const getHeaderKeys = () => {
    let headerGroup;

    if (proposalSummary?.proposalType === ProposalTypes.SplitDollar) {
      headerGroup = headerColumnGroupSDProjection;
    } else if (proposalSummary?.proposalType === ProposalTypes.PremiumFinance) {
      headerGroup = headerColumnGroupPFProjection;
    } else {
      headerGroup = headerColumnGroupProjection;
    }

    return Object.keys(headerGroup) as Array<keyof typeof headerColumnGroupProjection>;
  };

  const getColSpan = (key: string): number => {
    if (proposalSummary?.proposalType === ProposalTypes.SplitDollar) {
      return headerColumnGroupSDProjection[key as keyof typeof headerColumnGroupSDProjection]
        .length;
    }
    if (proposalSummary?.proposalType === ProposalTypes.PremiumFinance) {
      return headerColumnGroupPFProjection[key as keyof typeof headerColumnGroupPFProjection]
        .length;
    }
    return headerColumnGroupProjection[key as keyof typeof headerColumnGroupProjection].length;
  };

  const headerKeys = getHeaderKeys();

  const headerColumnGroupEle = (
    <Row>
      <Column
        header=""
        rowSpan={1}
        colSpan={1}
        style={{ background: isDark ? "#3A3A50" : "#EBEBEF" }}
        align="center"
        // headerStyle={{
        //   borderRightWidth: 1,
        //   borderRightColor: "var(--surface-100)",
        //   borderBottomWidth: 1,
        //   borderBottomColor: "var(--surface-100)",
        // }}
      />
      {headerKeys?.map((key: any) => (
        <Column
          header={key}
          key={key}
          rowSpan={1}
          colSpan={getColSpan(key)}
          style={{ background: isDark ? "#3A3A50" : "#EBEBEF" }}
          // headerStyle={{
          //   borderRightWidth: 1,
          //   borderRightColor: "var(--surface-100)",
          //   borderBottomWidth: 1,
          //   borderBottomColor: "var(--surface-100)",
          // }}
          align="center"
        />
      ))}
    </Row>
  );

  const getProjectionColumns = useCallback(
    (selectedData: any, chartFilter: any) => {
      if (proposalSummary?.proposalType === ProposalTypes.SplitDollar) {
        return projectionSDColumnJson(selectedData, chartFilter);
      }
      if (proposalSummary?.proposalType === ProposalTypes.KeyPersonInsurance) {
        return projectionKPColumnJson(selectedData, chartFilter);
      }
      if (proposalSummary?.proposalType === ProposalTypes.PremiumFinance) {
        return projectionPFColumnJson(selectedData, chartFilter);
      }
      return projectionColumnJson(selectedData, chartFilter);
    },
    [proposalSummary?.proposalType],
  );

  const onShareholdersChange = useCallback(
    async (
      selectedShareholderIds: string[],
      selectedFundingIds: string[],
      brandingColor: string,
    ) => {
      selectedProjectionData = selectedFundingIds;
      setLoading(true);
      setSelectedValues(selectedFundingIds);
      let newShareholderIds = selectedShareholderIds;
      if (proposalSummary?.proposalType === ProposalTypes.SplitDollar) {
        newShareholderIds = [];
      }
      setIsCurrPageLoading(true);
      const response = await makeRequest("interactiveProposalProjection").get(
        `${proposalId}?insuredIds=${JSON.stringify(newShareholderIds)}&filter=${JSON.stringify(
          selectedFundingIds,
        )}`,
        true,
        false,
      );
      if (response.status === 200) {
        const data = calculateChartData(
          response?.data?.data?.graphData,
          selectedFundingIds,
          brandingColor,
        );
        if (
          selectedShareholderIds.length === 0 &&
          proposalSummary?.proposalType === ProposalTypes.SplitDollar
        ) {
          setChartDataSets(null);
        } else {
          setChartDataSets(data);
        }
        setGraphData(response?.data?.data?.graphData);
        if (proposalSummary?.proposalType === ProposalTypes.SplitDollar) {
          const tableDataUpdated = response?.data?.data?.tableData.map((item: any) => ({
            ...item,
            planYear: item?.policyYear,
            year: item?.planYear,
          }));
          setTableData(tableDataUpdated || []);
        } else {
          setTableData(response?.data?.data?.tableData || []);
        }
      }
      setLoading(false);
      setIsCurrPageLoading(false, true);
    },
    [proposalId],
  );

  const accordionHeader = (title: string) => (
    <div className="text-lg conthrax-font ml-2 text-color-black">{title}</div>
  );

  const collapseIcon = (
    <div className="rounded-full bg-[#AEB0C6] p-[1px] cursor-pointer">
      <GoTriangleDown
        className="text-[#8A8AA3]/70"
        size={27}
        style={{ transition: "transform 0.2s" }}
      />
    </div>
  );
  const expandIcon = (
    <div className="rounded-full bg-[#AEB0C6] p-[1px] cursor-pointer">
      <GoTriangleUp
        className="text-[#8A8AA3]/70"
        size={27}
        style={{ transition: "transform 0.2s", rotate: "140deg" }}
      />
    </div>
  );

  const color = isDarkMode ? "#ffffff" : "#333333";

  const createTooltipHTML = (params: any) => {
    // Get current data point for dynamic values
    const currentData = graphData?.[params[0]?.dataIndex];
    const year = currentData?.year || 2025;
    const age = currentData?.insuredAge;
    const planYear = currentData?.planYear || 1;

    const header = `<div style="
      background: ${isDarkMode ? "#3A3A50" : "#E5E7EB"};
      color: ${isDarkMode ? color : "#374151"};
      padding: 8px 12px;
      text-align: center;
      font-weight: bold;
      font-family: 'Conthrax Semi-Bold', 'Arial', sans-serif;
      border-bottom: 1px solid ${isDarkMode ? "#4A4A60" : "#e0e0e0"};
      border-radius: 8px 8px 0 0;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 12px;
    ">
      <div style="display: flex; align-items: center; gap: 6px;">
        <i class="pi pi-calendar" style="font-size: 16px; color: ${
          globalTheme === THEME.DARK ? "#ffffff" : "#1F2937"
        }; font-weight: bold;"></i>
        <span style="font-weight: bold;">${year}</span>
      </div>
      <div style="width: 1px; height: 20px; background: ${
        globalTheme === THEME.DARK ? "#ffffff" : "#9CA3AF"
      }; opacity: 0.4;"></div>
      <div style="display: flex; align-items: center; gap: 6px;">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style="color: ${
          globalTheme === THEME.DARK ? "#ffffff" : "#1F2937"
        }; transform: translateY(1px);">
          <rect x="4" y="12" width="16" height="3" stroke="currentColor" stroke-width="1.5" fill="none"/>
          <rect x="6" y="8" width="12" height="4" stroke="currentColor" stroke-width="1.5" fill="none"/>
          <rect x="8" y="4" width="8" height="4" stroke="currentColor" stroke-width="1.5" fill="none"/>
          <line x1="12" y1="2" x2="12" y2="4" stroke="currentColor" stroke-width="1.5"/>
          <path d="M11 2L12 1L13 2" stroke="currentColor" stroke-width="1.5" fill="none"/>
        </svg>
        <span style="font-weight: bold;">${planYear}</span>
      </div>
      <div style="width: 1px; height: 20px; background: ${
        isDarkMode ? "#ffffff" : "#9CA3AF"
      }; opacity: 0.4;"></div>
      ${
        age
          ? `<div style="display: flex; align-items: center; gap: 6px;">
        <i class="pi pi-user" style="font-size: 16px; color: ${
          isDarkMode ? "#ffffff" : "#1F2937"
        }; font-weight: bold;"></i>
        <span style="font-weight: bold;">A${age}</span>
      </div>`
          : ""
      }
    </div>`;

    let content = '<div style="padding: 8px 12px;">';

    params.forEach((param: any) => {
      if (param.value !== null && param.value !== undefined) {
        const bulletColor = param.color || "#4A90E2";
        const isHighlighted = param.seriesName === selectedSeriesRef.current;

        content += `<div style="
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 4px 8px;
          margin: 2px 0;
          border-bottom: 1px solid ${isDarkMode ? "#4A4A60" : "#E5E7EB"};
          ${isHighlighted ? `background: ${tooltipColors.primaryColor}20; border-radius: 4px;` : ""}
        ">
          <div style="display: flex; align-items: center; gap: 8px;">
            <div style="
              width: 8px;
              height: 8px;
              border-radius: 50%;
              background-color: ${bulletColor};
              flex-shrink: 0;
            "></div>
            <span style="
              color: ${
                isHighlighted
                  ? tooltipColors.secondaryColor
                  : globalTheme === THEME.DARK
                  ? "#ffffff"
                  : "#374151"
              };
              font-family: ${
                isHighlighted ? "'Conthrax Semi-Bold', 'Arial', sans-serif" : "inherit"
              };
              font-size: ${isHighlighted ? "14px" : "12px"};
              font-weight: ${isHighlighted ? "bold" : "normal"};
            ">${param.seriesName}</span>
          </div>
          <span style="
            color: ${
              isHighlighted
                ? tooltipColors.secondaryColor
                : globalTheme === THEME.DARK
                ? "#ffffff"
                : "#374151"
            };
            margin-left: 12px;
            font-family: ${isHighlighted ? "'Conthrax Semi-Bold', 'Arial', sans-serif" : "inherit"};
            font-size: ${isHighlighted ? "14px" : "12px"};
            font-weight: ${isHighlighted ? "bold" : "normal"};
            display: inline-block;
            text-align: left;
            min-width: 80px;
            flex-shrink: 0;
          ">$${getUSANumberFormat(param.value, false, true)}</span>
        </div>`;
      }
    });

    content += "</div>";
    return `<div style="
      background: ${isDarkMode ? "#2A2A3D" : "#ffffff"};
      border: 1px solid ${isDarkMode ? "#3A3A50" : "#E5E7EB"};
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      overflow: hidden;
    ">${header}${content}</div>`;
  };

  const getChartTitle = () => {
    if (proposalSummary?.proposalType === ProposalTypes.KeyPersonInsurance) {
      return "Projected Value for Selected Employees";
    }
    if (proposalSummary?.proposalType === ProposalTypes.SplitDollar) {
      return `Projected Value for ${insuredDetails.firstName} ${insuredDetails.lastName}`;
    }
    if (proposalSummary?.proposalType === ProposalTypes.PremiumFinance) {
      return "Projected Value for Insured Persons";
    }
    return "Projected Value for Selected Shareholders";
  };

  const chartContent = () => {
    if (
      proposalSummary?.proposalType === ProposalTypes.PremiumFinance &&
      selectedValues.includes(ProjectionPFFilters.OUTSIDE_COLLATERAL_REQUIREMENT)
    ) {
      return (
        <OutsideCollateralRequirement
          isDarkMode={isDarkMode}
          selectedSeriesRef={selectedSeriesRef}
          zoomStateRef={zoomStateRef}
          color={color}
          graphData={graphData}
          selectedChartFilter={selectedChartFilter}
        />
      );
    }
    if (chartDataSets) {
      return (
        <div style={{ width: "100%", height: "365px", margin: 0, padding: 0 }} className="w-full">
          <ReactECharts
            onEvents={{
              click: (params: any) => {
                if (params.seriesName) {
                  selectedSeriesRef.current = params.seriesName;
                }
              },
              mouseover: (params: any) => {
                if (params.seriesName) {
                  selectedSeriesRef.current = params.seriesName;
                }
              },
              mouseout: () => {
                selectedSeriesRef.current = null;
              },
              dataZoom: (params: any) => {
                zoomStateRef.current = { start: params.start, end: params.end };
              },
            }}
            option={{
              title: {
                text: getChartTitle(),
                bottom: 40,
                textStyle: {
                  color,
                  fontSize: 14,
                },
              },
              tooltip: {
                trigger: "axis",
                axisPointer: {
                  type: "none",
                },
                backgroundColor: isDarkMode ? "#2A2A3D" : "#ffffff",
                borderColor: isDarkMode ? "#3A3A50" : "#e0e0e0",
                borderWidth: 1,
                borderRadius: 8,
                padding: 0,
                textStyle: {
                  color,
                  fontSize: 12,
                },
                formatter(params: any) {
                  if (!params || params.length === 0) return "";
                  // const year = params[0].axisValue;
                  // const yearLabel = `Year ${year}`;

                  return createTooltipHTML(params);
                },
                extraCssText: `
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
              border: none !important;
            `,
              },
              legend: {
                show: false,
              },
              grid: {
                left: "3%",
                right: "3%",
                bottom: "25%",
                top: "5%",
                containLabel: true,
                show: false,
              },
              dataZoom: [
                {
                  type: "slider",
                  show: true,
                  start: zoomStateRef.current?.start || 0,
                  end:
                    zoomStateRef.current?.end ||
                    Math.min(100, (10 / (graphData?.length || 1)) * 100),
                  minValueSpan: 1,
                  maxValueSpan: graphData?.length || 1,
                  height: 20,
                  bottom: 10,
                  borderColor: "transparent",
                  fillerColor: isDarkMode ? "rgba(167,183,204,0.3)" : "rgba(167,183,204,0.4)",
                  handleStyle: {
                    color: isDarkMode ? "#ffffff" : "#333333",
                    borderColor: isDarkMode ? "#4A4A60" : "#E5E7EB",
                  },
                  textStyle: {
                    color: `${color}`,
                    fontSize: 10,
                  },
                  showDetail: true,
                  showDataShadow: true,
                  realtime: true,
                  rangeMode: ["percent", "percent"],
                  filterMode: "filter",
                  xAxisIndex: 0,
                },
              ],
              xAxis: {
                type: "category",
                data: getChartLabels(graphData, selectedChartFilter),
                axisLabel: {
                  color: `${color}`,
                  fontFamily: "Conthrax Semi-Bold",
                  fontSize: 10,
                },
                boundaryGap: false,
                axisLine: {
                  show: false,
                  lineStyle: {
                    color,
                  },
                  onZero: true,
                },
                axisTick: {
                  alignWithLabel: true,
                },
              },
              yAxis: {
                type: "value",
                name: "Amount",
                nameLocation: "middle",
                nameGap: 60,
                nameTextStyle: {
                  color: `${color}`,
                  fontSize: 12,
                },
                splitLine: {
                  show: false,
                },
                max(value: any) {
                  return Math.round(value.max * 1.2);
                },
                min: 0,
                axisLabel: {
                  color: `${color}`,
                  formatter: (value: number) => `$${intToString(value)}`,
                  fontFamily: "Conthrax Semi-Bold",
                  fontSize: 10,
                },
                boundaryGap: [0, "100%"],
                axisLine: {
                  show: true,
                  lineStyle: {
                    color: `${color}`,
                  },
                },
              },
              series: chartDataSets.map((dataset: any) => ({
                ...dataset,
                name: dataset.label,
                type: dataset.type || "line",
                data: dataset.data,
                smooth: true,
                showSymbol: false,
                lineStyle: {
                  color: dataset.borderColor,
                },
                itemStyle: {
                  ...dataset.itemStyle,
                  color: dataset.borderColor,
                },
                areaStyle: dataset.fill
                  ? {
                      color: {
                        type: "linear",
                        x: 0,
                        y: 0,
                        x2: 0,
                        y2: 1,
                        colorStops: [
                          {
                            offset: 0,
                            color: `${dataset.borderColor.substring(0, 7)}`, // Semi-transparent at top
                          },
                          {
                            offset: 0.5,
                            color: `${dataset.borderColor.substring(0, 7)}70`, // Medium transparency in middle
                          },
                          {
                            offset: 1,
                            color: `${dataset.borderColor.substring(0, 7)}00`, // Transparent at bottom
                          },
                        ],
                      },
                    }
                  : undefined,
              })),
            }}
            style={{ width: "100%", height: "100%", margin: 0, padding: 0 }}
          />
        </div>
      );
    }
    return (
      <div className="flex justify-center items-center h-full">
        <p className="text-lg text-center">No data available</p>
      </div>
    );
  };

  const mainContent = () => {
    if (loading) {
      return <ScreenLoader />;
    }
    return (
      <div>
        <Accordion
          collapseIcon={collapseIcon}
          expandIcon={expandIcon}
          multiple
          activeIndex={[0, 1]}
          className="bg-gray-100 dark:!bg-[#111111] rounded-md ip-project-accordion-main"
        >
          <AccordionTab
            headerTemplate={accordionHeader("Projections Chart")}
            headerClassName="interactive-proposal-projection"
            className="bg-light-primary dark:bg-[#2A2A3D] rounded-md"
            style={{ padding: 0 }}
          >
            <CommentLayout
              commentLayoutId="interactive-proposal-projection-chart"
              style={{ margin: 0, padding: 0 }}
            >
              {chartContent()}
              <div className="mt-2">
                <ChartFilter />
              </div>
            </CommentLayout>
          </AccordionTab>
          <AccordionTab
            headerTemplate={accordionHeader("Projections Table")}
            headerClassName="interactive-proposal-projection"
            className="bg-light-primary dark:bg-[#2A2A3D] rounded-md"
          >
            {/* Projection Table */}
            <CommentLayout
              commentLayoutId="interactive-proposal-projection-table"
              className="flex justify-between w-full mt-1 "
            >
              {!loading && (
                <div className="w-full">
                  <InsuredTable
                    columns={getProjectionColumns(selectedProjectionData, selectedChartFilter)}
                    data={tableData}
                    hoverColor={isDarkMode ? secondaryColor : primaryColor}
                    columnGroup={
                      proposalSummary?.proposalType !== ProposalTypes.KeyPersonInsurance
                        ? headerColumnGroupEle
                        : null
                    } // Pass the header structure
                    scrollable
                    scrollHeight="600px"
                    className={
                      proposalSummary?.proposalType === ProposalTypes.KeyPersonInsurance ||
                      proposalSummary?.proposalType === ProposalTypes.BuySell
                        ? kpProjectionTableClassName
                        : ""
                    }
                  />
                  <p className="text-lg text-center my-5">{getChartTitle()}</p>
                </div>
              )}
            </CommentLayout>
          </AccordionTab>
        </Accordion>
      </div>
    );
  };

  const getFilterOptions = () => {
    if (proposalSummary?.proposalType === ProposalTypes.SplitDollar) {
      return ProjectionSDFilters;
    }
    if (proposalSummary?.proposalType === ProposalTypes.KeyPersonInsurance) {
      return ProjectionKPFilters;
    }
    if (proposalSummary?.proposalType === ProposalTypes.PremiumFinance) {
      return ProjectionPFFilters;
    }
    return ProjectionFilters;
  };

  return (
    <CommentLayout
      commentLayoutId="interactive-proposal-funding"
      className={`${
        !loading ? "rounded-2xl" : "rounded-t-2xl rounded-b-md"
      }  w-full shadow-lg bg-gray-100 dark:!bg-[#111111] `}
    >
      {/* Filter container */}
      <div
        className={`p-3 flex justify-between shrink-0 w-ful ${
          !loading ? "mb-1 rounded-t-md rounded-b-md" : ""
        } border-b-2 border-gray-300 dark:!border-[#3A3A50] bg-light-primary dark:bg-[#2A2A3D] `}
      >
        <InsuredFilter
          onShareholdersChange={onShareholdersChange}
          brandingColor={primaryColor}
          label="Values"
          filter={getFilterOptions()}
        />
      </div>
      {/*  Content container */}
      {mainContent()}
      <div className="hidden">{JSON.stringify(settingThemeData)}</div>
    </CommentLayout>
  );
};
export default InteractiveProposalProjection;
